import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { PERMISSIONS } from '../../constants/permissions';
import { ICreateUdEntitlementLineRequest, IUdEntitlementLine, MaterialType, UdStatus } from '../../interfaces/commercial.interface';
import UdUtilizationDialog from './UdUtilizationDialog';
import { enumLabel } from '../../utils/garmentsLabels';

const MATERIAL_TYPES: MaterialType[] = ['FABRIC', 'YARN', 'TRIMS', 'ACCESSORIES', 'PACKING', 'OTHER'];

const EMPTY_LINE: ICreateUdEntitlementLineRequest = {
    materialType: 'FABRIC', description: '', hsCode: '', uom: '', entitledQty: 0, unitPrice: null, currencyCode: 'USD',
};

interface Props {
    institutionId: string;
    udId: string;
    udStatus: UdStatus;
}

/** Entitlement lines for one UD register, each with a per-line balance bar. The bar reads
 * `line.balanceQty` / `line.entitledQty` as returned by the API -- it never recomputes the
 * balance client-side (brief's binding test). */
const UdEntitlementLinePanel: React.FC<Props> = ({ institutionId, udId, udStatus }) => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.UD_MANAGE);
    const [lines, setLines] = useState<IUdEntitlementLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<IUdEntitlementLine | null>(null);
    const [form, setForm] = useState<ICreateUdEntitlementLineRequest>(EMPTY_LINE);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [utilLine, setUtilLine] = useState<IUdEntitlementLine | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await UdRegisterService.listLines(institutionId, udId);
            setLines(res.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load entitlement lines');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [institutionId, udId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = () => { setEditing(null); setForm(EMPTY_LINE); setDialogOpen(true); };
    const openEdit = (line: IUdEntitlementLine) => {
        setEditing(line);
        setForm({
            materialType: line.materialType, description: line.description ?? '', hsCode: line.hsCode ?? '',
            uom: line.uom, entitledQty: line.entitledQty, unitPrice: line.unitPrice, currencyCode: line.currencyCode,
        });
        setDialogOpen(true);
    };

    const valid = form.uom.trim().length > 0 && form.entitledQty >= 0.001 && form.currencyCode.trim().length === 3;

    const saveWhy = form.uom.trim().length === 0 ? 'Enter the unit of measure.'
        : form.entitledQty < 0.001 ? 'Enter an entitled quantity of at least 0.001.'
        : form.currencyCode.trim().length !== 3 ? 'Enter a 3-letter currency code.'
        : null;

    const handleSave = async () => {
        try {
            if (editing) {
                await UdRegisterService.updateLine(institutionId, udId, editing.id, form);
                toast.showSuccess('Entitlement line updated');
            } else {
                await UdRegisterService.createLine(institutionId, udId, form);
                toast.showSuccess('Entitlement line added');
            }
            setDialogOpen(false);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save entitlement line');
        }
    };

    const handleDelete = async (line: IUdEntitlementLine) => {
        try {
            await UdRegisterService.deleteLine(institutionId, udId, line.id);
            toast.showSuccess('Entitlement line removed');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to remove entitlement line');
        }
    };

    const canUtilize = udStatus === 'APPROVED';

    return (
        <Paper data-help-section="commercial.udLines.table" sx={{ p: 2, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">Entitlement Lines</Typography>
                {canManage && (
                    <Button data-help="commercial.udLines.add" size="small" startIcon={<AddIcon />} onClick={openCreate}>Add Line</Button>
                )}
            </Box>

            {!canUtilize && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Utilization can only be recorded once this UD is APPROVED.
                </Typography>
            )}

            {!loading && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.udLines.col.material">Material</TableCell>
                                <TableCell data-help="commercial.udLines.col.description">Description</TableCell>
                                <TableCell align="right" data-help="commercial.udLines.col.entitled">Entitled</TableCell>
                                <TableCell align="right" data-help="commercial.udLines.col.utilized">Utilized</TableCell>
                                <TableCell data-help="commercial.udLines.col.balance" sx={{ minWidth: 160 }}>Balance</TableCell>
                                {canManage && <TableCell align="right" data-help="commercial.udLines.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map(line => {
                                const pct = line.entitledQty > 0 ? Math.min(100, (line.utilizedQty / line.entitledQty) * 100) : 0;
                                return (
                                    <TableRow key={line.id} data-testid={`ud-line-${line.id}`}>
                                        <TableCell>{enumLabel(line.materialType)}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{line.description ?? '—'}</TableCell>
                                        <TableCell align="right">{line.entitledQty} {line.uom}</TableCell>
                                        <TableCell align="right">{line.utilizedQty} {line.uom}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LinearProgress
                                                    variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                                    data-testid={`ud-line-balance-bar-${line.id}`}
                                                    aria-label={`balance ${line.balanceQty} ${line.uom}`}
                                                />
                                                <Typography variant="caption" data-testid={`ud-line-balance-qty-${line.id}`}>
                                                    {line.balanceQty} {line.uom}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        {canManage && (
                                            <TableCell align="right">
                                                <Tooltip title="Record utilization">
                                                    <span>
                                                        <IconButton data-help="commercial.udLines.utilize" size="small" disabled={!canUtilize} onClick={() => setUtilLine(line)}>
                                                            <PlaylistAddIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <IconButton data-help="commercial.udLines.edit" size="small" onClick={() => openEdit(line)}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton data-help="commercial.udLines.remove" size="small" onClick={() => handleDelete(line)}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                            {lines.length === 0 && (
                                <TableRow><TableCell colSpan={canManage ? 6 : 5} align="center">No entitlement lines yet.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.udLines.dialog.page' } as any}>
                <DialogTitle>{editing ? 'Edit Entitlement Line' : 'Add Entitlement Line'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField data-help="commercial.udLines.materialType" select label="Material Type" value={form.materialType}
                        onChange={e => setForm({ ...form, materialType: e.target.value as MaterialType })}>
                        {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                    </TextField>
                    <TextField data-help="commercial.udLines.description" label="Description" value={form.description ?? ''}
                        onChange={e => setForm({ ...form, description: e.target.value })} />
                    <TextField data-help="commercial.udLines.hsCode" label="HS Code" value={form.hsCode ?? ''}
                        onChange={e => setForm({ ...form, hsCode: e.target.value })} />
                    <TextField data-help="commercial.udLines.uom" label="UOM" required value={form.uom}
                        onChange={e => setForm({ ...form, uom: e.target.value })} />
                    <TextField data-help="commercial.udLines.entitledQty" label="Entitled Qty" type="number" required value={form.entitledQty}
                        onChange={e => setForm({ ...form, entitledQty: Number(e.target.value) })} />
                    <TextField data-help="commercial.udLines.unitPrice" label="Unit Price (optional)" type="number" value={form.unitPrice ?? ''}
                        onChange={e => setForm({ ...form, unitPrice: e.target.value === '' ? null : Number(e.target.value) })} />
                    <TextField data-help="commercial.udLines.currency" label="Currency Code" required value={form.currencyCode}
                        onChange={e => setForm({ ...form, currencyCode: e.target.value.toUpperCase() })} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.udLines.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.udLines.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={!valid} onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>

            {utilLine && (
                <UdUtilizationDialog
                    institutionId={institutionId}
                    udId={udId}
                    line={utilLine}
                    onClose={() => setUtilLine(null)}
                    onSaved={async () => { setUtilLine(null); await load(); }}
                />
            )}
        </Paper>
    );
};

export default UdEntitlementLinePanel;
